'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useForm, useWatch } from 'react-hook-form'

import { TFindID, createZFindID } from '@/entities/auth'
import { Button, Form } from '@/shared/ui'

import { useFindID } from '../api/find-id'

const defaultValues: TFindID = {
  name: '',
  phoneNumber: '',
}

export const FindIDForm = ({
  onSuccess,
}: {
  onSuccess: (username: string) => void
}) => {
  const t = useTranslations('find_id')

  const form = useForm<TFindID>({
    resolver: zodResolver(createZFindID(t)),
    defaultValues,
    reValidateMode: 'onSubmit',
  })

  const [name, phoneNumber] = useWatch({
    control: form.control,
    name: ['name', 'phoneNumber'],
  })

  const isDisabled = !name || !phoneNumber

  const findID = useFindID({
    form,
    onSuccess: ({ username }) => {
      onSuccess(username)
    },
  })

  const submitForm = (data: TFindID) => {
    findID.execute(data)
  }

  return (
    <Form.Root form={form}>
      <Form.Field name="name">
        <Form.Label className="font-medium text-black">
          {t('name_label')}
        </Form.Label>
        <Form.TextField
          type="text"
          placeholder={t('name_placeholder')}
          fullWidth
        />
        <Form.Message />
      </Form.Field>

      <Form.Field name="phoneNumber" className="mt-6">
        <Form.Label className="font-medium text-black">
          {t('phone_number_label')}
        </Form.Label>
        <Form.TextField
          type="tel"
          placeholder={t('phone_number_placeholder')}
          fullWidth
        />
        <Form.Message className="whitespace-break-spaces" />
      </Form.Field>

      <Button
        size="large"
        className="mt-10 mb-2.5"
        type="submit"
        fullWidth
        onClick={form.handleSubmit(submitForm)}
        disabled={isDisabled}
      >
        {t('button.confirm')}
      </Button>
    </Form.Root>
  )
}
